/**
 * Unified Modal Store
 *
 * Single source of truth for non-primary modal open/close state.
 * Data-bearing state (critique issues, generate results, phrase data, etc.)
 * lives in their respective stores; only the boolean open/close flags live here.
 */

import { create } from 'zustand';

export type ModalKey =
  | 'generate'
  | 'critique'
  | 'compositionTools'
  | 'saveProgression'
  | 'myProgressions'
  | 'auth'
  | 'settings'
  | 'keyboardShortcuts'
  | 'snippets'
  | 'learnPiece';

interface ModalState {
  open: Partial<Record<ModalKey, boolean>>;
  openModal: (key: ModalKey) => void;
  closeModal: (key: ModalKey) => void;
  toggleModal: (key: ModalKey) => void;
  isOpen: (key: ModalKey) => boolean;
}

export const useModalStore = create<ModalState>((set, get) => ({
  open: {},
  // Single-active-modal: opening one closes any others to prevent stacking (b205ddf4)
  openModal: (key) =>
    set((s) => ({
      open: {
        ...Object.fromEntries(Object.keys(s.open).map((k) => [k, false])),
        [key]: true,
      },
    })),
  closeModal: (key) => set((s) => ({ open: { ...s.open, [key]: false } })),
  toggleModal: (key) =>
    set((s) => ({ open: { ...s.open, [key]: !s.open[key] } })),
  isOpen: (key) => !!get().open[key],
}));

/**
 * Escape closes whichever modal-store modal is currently open — solved ONCE
 * here rather than in each modal component (b205ddf4). Since openModal
 * enforces single-active-modal exclusivity, at most one key is ever true, so
 * there's no ambiguity about which one Escape should close. Individual
 * modals that migrate onto this store should drop their own Escape
 * listeners (see KeyboardShortcutsGuide.tsx) to avoid a redundant duplicate
 * handler running alongside this one.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    const state = useModalStore.getState();
    const openKey = (Object.keys(state.open) as ModalKey[]).find((k) => state.open[k]);
    if (openKey) state.closeModal(openKey);
  });
}
